export const companyData = {
  name: "BERKSHIRE INVESTMENT GROUP INC.",
  shortName: "BIG",
  tagline: "Permanent capital partner for institutional private credit and infrastructure platforms",
  contactEmail: "info@berkshire.inc",
  logo: "https://customer-assets.emergentagent.com/job_partner-form/artifacts/gmy4lv0n_Berkshire%20Logo.jpg",
  permanentCapital: "$500M",
  corePlatforms: "5-7",
  ownershipRange: "10-49%",
  checkSize: "$50M+"
};

export const aboutSection = {
  title: "Purpose-Built for Long-Term Partnership",
  description: [
    "Berkshire Investment Group is a permanent-capital investment holding company acquiring minority, governance-oriented stakes in institutional private credit and infrastructure platforms across developed markets. We align patient capital with durable asset-management franchises, rejecting short-term, exit-driven outcomes.",
    "Capitalized with $500 million USD of permanent equity, BIG operates unlevered with no fund lives, capital recycling requirements, or exit deadlines. This structure enables long-term franchise value underwriting based on recurring cash generation and governance quality."
  ],
  stats: [
    { value: "$500M", label: "Permanent Capital", sublabel: "Deployed from balance sheet" },
    { value: "5-7", label: "Core Platforms", sublabel: "Concentrated portfolio approach" }
  ]
};

export const investmentFocus = {
  title: "Strategic Investment Mandate",
  description: "BIG targets a concentrated portfolio of private credit and infrastructure managers with institutional operating standards, conservative risk cultures, and responsible scaling capacity. Our focus centers on platforms serving institutional LP bases in developed markets.",
  features: [
    {
      icon: "shield",
      title: "Conservative Risk Culture",
      description: "Platforms with proven institutional operating standards and disciplined risk management"
    },
    {
      icon: "trending-up",
      title: "Scalable Platforms",
      description: "Capacity to grow assets under management responsibly over time"
    },
    {
      icon: "globe",
      title: "Developed Markets",
      description: "Focus on strategies with real-economy cash flows, not mark-to-market volatility"
    }
  ]
};

export const capitalFramework = {
  title: "Disciplined Deployment Strategy",
  description: "BIG deploys $500 million equity across 5-7 core platforms, prioritizing alignment and governance over speed. Typical equity checks range from $50-100 million per platform, with flexibility for smaller initial investments or larger anchor positions.",
  subdescription: "We target minority ownership stakes between 10-49% at the platform level, providing meaningful economic participation and governance influence while preserving GP independence and entrepreneurial control.",
  stats: [
    { value: "10-49%", label: "Ownership Range", sublabel: "Minority stakes preserving GP control" },
    { value: "$50M+", label: "Check Size", sublabel: "Flexible per platform investment" }
  ]
};

export const investmentStructure = {
  title: "Investment Structure & Economic Exposure",
  description: "BIG structures investments to balance capital preservation with long-term upside participation. Our economic exposure is weighted toward recurring, predictable cash generation, reducing reliance on exit timing and ensuring viability across market cycles.",
  items: [
    {
      number: "01",
      title: "Management Company Equity",
      description: "Ownership providing participation in management fee income and long-term franchise value"
    },
    {
      number: "02",
      title: "Structured Capital Instruments",
      description: "Preferred equity or senior capital for enhanced downside protection and cash yield"
    },
    {
      number: "03",
      title: "Carried Interest Participation",
      description: "Select participation treated as incremental upside, not core underwriting assumption"
    }
  ]
};

export const strategicRole = {
  title: "Strategic Role as Long-Term Capital Partner",
  description: "BIG functions as an institutional stabilizer, not a strategy dictator. We provide permanent, repeatable capital supporting platform growth, withstanding market dislocations, and remaining committed through cycles without replacing LP bases or extracting short-term economics.",
  benefits: [
    { icon: "clock", title: "No Exit Pressure", description: "Free from fundraising calendars and capital call mechanics" },
    { icon: "calendar", title: "Long-Dated Support", description: "Compatible with structures traditional LPs cannot underwrite" },
    { icon: "growth", title: "Platform Growth", description: "Supporting bespoke vehicles and balance-sheet initiatives" }
  ]
};

export const governanceSection = {
  title: "Institutional Governance Without Interference",
  description: "BIG secures institutional governance rights commensurate with ownership position. These include board representation or observer rights, dilution protections, balance-sheet oversight, and approval rights over major strategic changes.",
  subdescription: "Governance protects the franchise and aligns long-term incentives without interfering with investment decisions or portfolio construction. GP principals retain meaningful ownership, ensuring both parties protect platform sustainability.",
  rights: [
    { icon: "users", title: "Board Participation", description: "Representation or observer rights aligned with ownership" },
    { icon: "eye", title: "Strategic Oversight", description: "Approval rights for major structural changes" },
    { icon: "shield-check", title: "Balance Sheet Protection", description: "Oversight of leverage and financial policy" }
  ]
};

export const capitalNetwork = {
  title: "Aligned LP Ecosystem & Co-Investment Access",
  description: "Beyond our permanent balance sheet, BIG operates alongside select long-duration institutional LPs sharing our investment philosophy and governance standards. These include UAE sovereign wealth institutions, leading family offices, and global long-term investors.",
  subdescription: "For partner GPs, engagement with BIG provides gateway access to stable, repeatable LP capital supporting fund commitments, co-investments, and platform growth across multiple cycles. Capital partners are curated for duration, reliability, and institutional compatibility.",
  partners: [
    { icon: "landmark", title: "Sovereign Capital", description: "UAE sovereign wealth institutions" },
    { icon: "building", title: "Family Offices", description: "Leading regional long-term investors" }
  ]
};

export const whyPartner = {
  title: "Why Top-Tier GPs Partner With BIG",
  intro: "Elite GPs don't struggle to raise capital—they struggle with LP fragmentation, misaligned time horizons, and capital that disappears during downturns. BIG addresses these issues by providing stable, non-disruptive capital that grows with the platform.",
  reasons: [
    {
      number: "1",
      title: "Permanent Aligned Capital",
      description: "No forced deployment windows, exit pressure, or style drift demands. BIG behaves as cornerstone partner, not transactional counterparty."
    },
    {
      number: "2",
      title: "Solving Real Friction",
      description: "Reduces LP fragmentation, repetitive re-underwriting, and shallow relationships while improving franchise stability."
    },
    {
      number: "3",
      title: "Governance as Protection",
      description: "Alignment without interference—no micro-management of portfolio construction or bespoke veto rights impeding execution."
    },
    {
      number: "4",
      title: "Expanding Optionality",
      description: "Support for fund anchoring, GP commitment financing, co-investments, continuation vehicles, and bespoke long-dated structures."
    },
    {
      number: "5",
      title: "Clean Economics",
      description: "No fee discounts, preferential carry grabs, or re-trades. Returns driven by duration and franchise quality, not extraction."
    },
    {
      number: "6",
      title: "Built for Cycles",
      description: "Structured to endure rate shocks, liquidity freezes, and institutional retrenchment. We don't withdraw when markets turn."
    }
  ]
};

export const closingSection = {
  title: "The Capital Partner GPs Would Design Themselves",
  quote: "GPs do not resist BIG because we don't compete with them, constrain them, second-guess them, destabilize their LP base, disappear in downturns, or extract value at their expense.",
  description: "BIG operates as the capital partner most GPs would design themselves—providing permanent capital, institutional governance, access to aligned LPs, and optionality without obligation. Partnership continues because it remains mutually beneficial, not because of structural coercion.",
  features: [
    { icon: "check-circle", title: "Credible to Best LPs", description: "Trusted by sovereigns, endowments, and pensions while remaining non-threatening to existing relationships" },
    { icon: "lock", title: "Discretion as Principle", description: "We don't market off GP brands, politicize positions, or disrupt LP dynamics" },
    { icon: "unlock", title: "Optionality Without Lock-In", description: "No obligation to allocate future funds or expand strategies mechanically" }
  ]
};

export const africaPlatform = {
  brandName: "Berkshire Africa Special Situations",
  shortName: "BASS",
  tagline: "Africa's permanent capital partner for restructuring, rescue financing, and strategic asset consolidation.",
  thesis: "Africa is capital constrained, not opportunity constrained. Sovereign stress, banking weakness, FX shortages, and refinancing gaps are creating a multi-decade restructuring cycle — and the opportunity to acquire strategic assets at substantial discounts to intrinsic value.",
  highlightStats: [
    { value: "$25–50M", label: "Phase 1 Capital Raise", sublabel: "Permanent capital base" },
    { value: "18–25%", label: "Target Net IRR", sublabel: "Across cycle, USD-denominated" },
    { value: "9", label: "Priority Markets", sublabel: "Tier 1 & Tier 2 across Africa" },
    { value: "5", label: "Strategic Sectors", sublabel: "Banking, power, agro, logistics, telecom" }
  ],
  pillars: [
    {
      tag: "Pillar I",
      title: "Rescue & Structured Lending",
      description: "Bridge loans, restructuring facilities, trade finance, working capital, and debtor rescue financing — collateralized with cash sweeps, warrants, and board observer rights.",
      points: ["15–25% USD yield", "Collateralized with export receivables", "Equity kickers + warrants"]
    },
    {
      tag: "Pillar II",
      title: "Distressed Asset Acquisition",
      description: "Acquire distressed infrastructure, failed industrial assets, banks, logistics, agro-processing, mining infrastructure, and power projects — then recapitalize, improve, refinance, and consolidate.",
      points: ["Buy debt below par", "Take operational control", "Consolidate fragmented sectors"]
    },
    {
      tag: "Pillar III",
      title: "Special Situations Advisory",
      description: "Act as arranger, restructuring advisor, liability manager, and capital structuring advisor — building proprietary deal flow and political access.",
      points: ["Retainers + success fees", "Syndication spreads", "Proprietary deal pipeline"]
    },
    {
      tag: "Pillar IV",
      title: "Trade Finance Platform",
      description: "The liquidity engine. Self-liquidating exposures across commodities, structured inventory, and receivables — producing recurring cash flow and counterparty intelligence.",
      points: ["Self-liquidating positions", "Recurring USD cash flow", "Acquisition pipeline from failures"]
    }
  ],
  geographies: {
    tier1: [
      { name: "Kenya", note: "Banking consolidation, fiber, infrastructure" },
      { name: "South Africa", note: "Restructuring depth, distressed equity" },
      { name: "Ghana", note: "Sovereign stress, refinancing gaps" },
      { name: "Egypt", note: "FX shortages, USD scarcity premium" },
      { name: "Nigeria", note: "Banking weakness, power, agro-processing" }
    ],
    tier2: [
      { name: "Zambia", note: "Sovereign restructuring, mining infra" },
      { name: "Angola", note: "Energy-linked refinancing" },
      { name: "Ethiopia", note: "Strategic asset divestments" },
      { name: "Mozambique", note: "Gas-to-power, logistics corridors" },
      { name: "Tanzania", note: "Agro-export, port logistics" }
    ]
  },
  sectors: [
    { title: "Banking & Financial Services", description: "Undercapitalized banks, regulatory recapitalization pressure, fragmented sector — best asymmetric opportunity." },
    { title: "Power & Energy Infrastructure", description: "Operational renewables, mini-grids, gas-to-power, transmission-linked infrastructure with refinancing pressure." },
    { title: "Agro-Industrial Platforms", description: "Rice mills, fertilizer blending, storage/silos, export processors, edible oils — strategic for GCC food-security capital." },
    { title: "Logistics & Ports", description: "Dry ports, trucking, warehousing, rail-linked assets, cold chain — hard-to-replace cash-flow assets." },
    { title: "Telecom & Fiber", description: "Distressed operators, tower portfolios, enterprise fiber, data centers — value in infrastructure separation, not subscribers." }
  ],
  whyWeWin: [
    { title: "Permanent capital", description: "Hold across cycles. No fund-life pressure." },
    { title: "Hybrid model", description: "Debt + equity + restructuring + operations under one roof." },
    { title: "Operator mindset", description: "Take operational control where banks and PE cannot." },
    { title: "Political access", description: "Restructure politically sensitive assets credibly." }
  ],
  ctas: {
    investor: {
      title: "Capital Partners",
      subtitle: "GCC family offices, sovereigns, DFIs, commodity traders & UHNWIs",
      blurb: "Co-invest in or anchor the Phase 1 permanent capital base. Target Net IRR 18–25%, structured for long-duration USD investors.",
      button: "Investor Inquiry"
    },
    situation: {
      title: "Distressed Situations",
      subtitle: "Companies, banks, asset holders & advisors with a restructuring need",
      blurb: "Bring us a situation: rescue financing, restructuring, distressed sale, bank recapitalization, or strategic asset divestment. We move quickly and decisively.",
      button: "Submit a Situation"
    }
  }
};
